import { ViewportSpinner } from '../common/ViewportSpinner';
import React, { lazy, Suspense, useState } from 'react';
import { isChunkLoadError, verifyAndRecoverChunkFailure } from '../../diagnostics/chunkLoadRecovery';

class LocalChunkBoundary extends React.Component<{ retry: () => void; children: React.ReactNode }, { error?: unknown }> {
  state: { error?: unknown } = {};
  static getDerivedStateFromError(error: unknown) { return { error }; }
  componentDidCatch(error: unknown) { if (isChunkLoadError(error)) void verifyAndRecoverChunkFailure(error, 'window.error'); }
  render() {
    if (!this.state.error) return this.props.children;
    if (!isChunkLoadError(this.state.error)) throw this.state.error;
    return <div role="alert" className="m-4 rounded-xl border bg-white p-4">
      <p>Ez az oldal most nem tölthető be. A kapcsolat helyreállása után próbáld újra.</p>
      <button className="mt-3 rounded-lg border px-3 py-2" onClick={this.props.retry}>Oldal betöltése újra</button>
    </div>;
  }
}

// Recreate React's rejected lazy wrapper on explicit retry; keep the Dashboard
// shell and its selected scope mounted. Successful module remains cached.
export function retryableLazy<T extends React.ComponentType<any>>(load: () => Promise<{ default: T }>): React.ComponentType<React.ComponentProps<T>> {
  let loaded: { default: T } | undefined;
  const attempt = () => lazy(async () => { if (loaded) return loaded; const value = await load(); loaded = value; return value; });
  return function RetryableRoute(props) {
    const [state, setState] = useState(() => ({ revision: 0, Component: attempt() }));
    const Component = state.Component;
    return <LocalChunkBoundary key={state.revision} retry={() => setState(previous => ({ revision: previous.revision + 1, Component: attempt() }))}>
      <Suspense fallback={<ViewportSpinner />}><Component {...props as any} /></Suspense>
    </LocalChunkBoundary>;
  };
}
